import { httpClient } from "../../lib/http-client";
import type { ScopeGroup } from "./scopes";
import { levelName, levelDescription } from "./scopeCopy";
import type {
  CreateSandboxInput,
  LeadChannel,
  LeadEntityType,
  LeadTarget,
  Sandbox,
  SandboxApiKey,
  SandboxStatus,
  TestLead,
} from "./types";
import { LEAD_COMBINATIONS } from "./types";

const BASE = "/pfdn/v1/sandbox/accounts";

interface RawAccount {
  id: string;
  name: string;
  country: string;
  status: SandboxStatus;
  created_at: string;
  updated_at: string;
  expires_at?: string | null;
  available_credits?: number | null;
  api_key_last_used_at?: string | null;
  api_key_last_used_at_known?: boolean;
}

interface RawApiKey {
  id: string;
  api_key: string;
  api_secret?: string;
  scopes?: string[] | null;
  created_at: string;
  expires_at?: string | null;
}

interface RawUser {
  id: number;
  first_name: string;
  last_name: string;
  pf_agent_id: string;
}

interface RawListing {
  id: string;
  title: string;
  reference: string;
}

interface RawScopeGroup {
  key: string;
  default_level: string;
  read_only: boolean;
  levels: string[];
}

interface RawLead {
  id: string;
  public_id: string;
  channel: string;
  status: string;
  created_at: string;
}

function toSandbox(raw: RawAccount): Sandbox {
  return {
    id: raw.id,
    name: raw.name,
    country: raw.country,
    status: raw.status,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    expiresAt: raw.expires_at ?? null,
    credits: raw.available_credits ?? null,
    lastUsedAt: raw.api_key_last_used_at ?? null,
    lastUsedKnown: raw.api_key_last_used_at_known ?? false,
  };
}

function toApiKey(raw: RawApiKey): SandboxApiKey {
  return {
    id: raw.id,
    apiKey: raw.api_key,
    apiSecret: raw.api_secret ?? "",
    scopes: raw.scopes ?? null,
    createdAt: raw.created_at,
    expiresAt: raw.expires_at ?? null,
  };
}

export async function fetchScopeGroups(): Promise<ScopeGroup[]> {
  const { data } = await httpClient.get<{ items: RawScopeGroup[] }>(
    "/pfdn/v1/sandbox/scope-groups",
  );
  return (data.items ?? []).map((group) => ({
    key: group.key,
    defaultLevel: group.default_level,
    readOnly: group.read_only,
    levels: group.levels.map((level) => ({
      key: level,
      name: levelName(level),
      description: levelDescription(group.key, level),
    })),
  }));
}

export interface SandboxList {
  sandboxes: Sandbox[];
  maxActive: number;
}

export async function listSandboxes(): Promise<SandboxList> {
  const { data } = await httpClient.get<{
    items: RawAccount[];
    max_active_accounts: number;
  }>(BASE);
  return {
    sandboxes: (data.items ?? []).map(toSandbox),
    maxActive: data.max_active_accounts,
  };
}

export async function createSandbox(input: CreateSandboxInput): Promise<Sandbox> {
  const { data } = await httpClient.post<RawAccount>(BASE, {
    name: input.name,
    country: input.country,
    expiry_period_in_days: input.expiryPeriodInDays,
    scopes: input.scopes,
  });
  return toSandbox(data);
}

export async function deleteSandbox(id: string): Promise<void> {
  await httpClient.delete(`${BASE}/${id}`);
}

export async function listApiKeys(accountId: string): Promise<SandboxApiKey[]> {
  const { data } = await httpClient.get<{ items: RawApiKey[] }>(
    `${BASE}/${accountId}/api-keys`,
  );
  return (data.items ?? []).map(toApiKey);
}

export async function searchTargets(
  accountId: string,
  entityType: LeadEntityType,
  query: string,
): Promise<LeadTarget[]> {
  const path = entityType === "agent" ? "users" : "listings";
  const { data } = await httpClient.get<{
    items: (RawUser | RawListing)[];
  }>(`${BASE}/${accountId}/${path}`, { params: { query, limit: 20 } });

  return (data.items ?? []).map((item) =>
    "pf_agent_id" in item
      ? {
          id: item.pf_agent_id,
          label: `${item.first_name} ${item.last_name}`.trim(),
          meta: item.pf_agent_id,
        }
      : { id: item.id, label: item.title, meta: item.reference },
  );
}

// The sandbox models no lead sender; pf-broker sends this same constant.
export const SENDER = {
  name: "Test User",
  email: "test.user@sandbox.pf.dev",
  phone: "+971501234567",
};

const MESSAGE =
  "Hi, I am interested in this property. Could you please provide more details about pricing and availability?";

export async function createTestLead(
  accountId: string,
  entityType: LeadEntityType,
  target: LeadTarget,
  channel: LeadChannel,
): Promise<TestLead> {
  const { data } = await httpClient.post<RawLead>(`${BASE}/${accountId}/leads`, {
    // pf_agent_id reads "ag-5512"; the sandbox wants the numeric part.
    entity: {
      id: entityType === "agent" ? target.id.split("-").pop() : target.id,
      type: entityType,
    },
    leadType: LEAD_COMBINATIONS[entityType].leadType,
    channel,
    sender: SENDER,
    message: MESSAGE,
  });

  return {
    id: data.id,
    publicId: data.public_id,
    channel: data.channel,
    status: data.status,
    createdAt: data.created_at,
  };
}
